import React, { useEffect } from "react";
import CurrencyInput from "react-currency-input-field";

import { PropertyFormData } from "@/components/form-property/property-types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CaptadorSelector } from "./captadorSelector";
import { RegionSelector } from "../region/RegionSelector";

// Tipo de região vinda do backend
export interface Region {
  id: number;
  attributes: {
    Name: string;
    Observation?: string;
  };
}

interface AddressProps {
  data: PropertyFormData;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<PropertyFormData>>;
}

export function Address({ data, onChange, setFormData }: AddressProps) {
  const handleRegionSelect = (region: Region) => {
    onChange({
      target: {
        name: "region",
        value: region.attributes.Name,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  useEffect(() => {
    const location = data.keyLocation || "";
    const complement = data.keyComplement || "";
    let combinedKeys = "";

    if (location && complement) {
      combinedKeys = `${location} - ${complement}`;
    } else if (location) {
      combinedKeys = location;
    }

    // Só atualiza se o valor mudou para evitar loops infinitos
    if (combinedKeys !== data.keys) {
      onChange({
        target: { name: "keys", value: combinedKeys },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [data.keyLocation, data.keyComplement, data.keys, onChange]);

  // Ao mudar o CEP, simplesmente repassa para o "onChange"
  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
  };

  // Busca endereço via ViaCEP ao digitar CEP
  useEffect(() => {
    const fetchAddressFromCep = async (cep: string) => {
      const sanitizedCep = cep.replace(/\D/g, "");
      if (sanitizedCep.length === 8) {
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${sanitizedCep}/json/`,
          );
          const addressData = await response.json();

          if (!addressData.erro) {
            onChange({
              target: {
                name: "street",
                value: addressData.logradouro || "",
              },
            } as React.ChangeEvent<HTMLInputElement>);
          } else {
            console.error("CEP não encontrado.");
          }
        } catch (error) {
          console.error("Erro ao buscar endereço:", error);
        }
      }
    };

    if (data.zipCode) {
      fetchAddressFromCep(data.zipCode);
    }
  }, [data.zipCode, onChange]);

  return (
    <div className="w-full bg-gray-50 p-6 sm:p-8">
      <h1 className="mb-6 text-center text-3xl font-bold text-gray-800">
        Detalhes do Imóvel
      </h1>
      {/* Status */}
      <div className="flex flex-row space-x-5">
        <div className="mb-5 flex w-64 flex-col">
          <Label htmlFor="street" className="text-gray-600">
            Código da Kenlo
          </Label>
          <Input
            id="tagKanlo"
            name="tagKanlo"
            placeholder="Ex: AP1000"
            value={data.tagKanlo || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>
        <div className="mb-5 flex w-64 flex-col">
          <Label htmlFor="status" className="text-gray-600">
            Status
          </Label>
          <Select
            value={data.status || ""}
            onValueChange={(value: string) =>
              onChange({
                target: { name: "status", value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          >
            <SelectTrigger id="status" name="status" className="mt-1">
              <SelectValue placeholder="Selecione o status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Ativo">Ativo</SelectItem>
              <SelectItem value="Vendido">Vendido</SelectItem>
              <SelectItem value="Suspenso">Suspenso</SelectItem>
              <SelectItem value="Locação">Locação</SelectItem>
              <SelectItem value="Disponível">Disponível</SelectItem>
              <SelectItem value="Lançamento">Lançamento</SelectItem>
              <SelectItem value="Cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mb-5 flex w-64 flex-col">
          <Label htmlFor="lado" className="text-gray-600">
            Lado
          </Label>
          <Select
            value={data.lado || ""}
            onValueChange={(value: string) =>
              onChange({
                target: { name: "lado", value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          >
            <SelectTrigger id="lado" name="lado" className="mt-1">
              <SelectValue placeholder="Selecione o lado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="P">P</SelectItem>
              <SelectItem value="M">M</SelectItem>
              <SelectItem value="O">O</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* SEÇÃO: Localização */}
      <h2 className="mb-4 text-xl font-semibold text-gray-700">Localização</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* CEP */}
        <div className="flex flex-col">
          <Label htmlFor="zipCode" className="text-gray-600">
            CEP
          </Label>
          <Input
            type="text"
            id="zipCode"
            name="zipCode"
            placeholder="Ex: 12345678"
            value={data.zipCode || ""}
            onChange={handleCepChange}
            maxLength={8}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="street" className="text-gray-600">
            Rua
          </Label>
          <Input
            id="street"
            name="street"
            placeholder="Ex: Av. Brasil"
            value={data.street || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="number" className="text-gray-600">
            Número
          </Label>
          <Input
            id="number"
            name="number"
            placeholder="Ex: 123"
            value={data.number || ""}
            onChange={onChange}
            className="mt-1 w-32"
          />
        </div>

        {/* Região com busca dinâmica */}
        <div className="flex flex-col">
          <RegionSelector
            onSelect={handleRegionSelect}
            selectedValue={data.region || ""}
            required={true}
          />
        </div>
        <div className="flex flex-col">
          <Label htmlFor="neighborhood" className="text-gray-600">
            Bairro
          </Label>
          <Input
            id="neighborhood"
            name="neighborhood"
            placeholder="Ex: Centro"
            value={data.neighborhood || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="city" className="text-gray-600">
            Cidade
          </Label>
          <Input
            id="city"
            name="city"
            placeholder="Ex: Santos"
            value={data.city || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>
      </div>
      {/* SEÇÃO: Áreas */}
      <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-700">Áreas</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
        <div className="flex flex-col">
          <Label htmlFor="totalArea" className="text-gray-600">
            Área Total (m²)
          </Label>
          <Input
            id="totalArea"
            name="totalArea"
            placeholder="Ex: 200"
            value={data.totalArea || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="usableArea" className="text-gray-600">
            Área Útil (m²)
          </Label>
          <Input
            id="usableArea"
            name="usableArea"
            placeholder="Ex: 150"
            value={data.usableArea || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="areaConstruida" className="text-gray-600">
            Área Construída (m²)
          </Label>
          <Input
            id="areaConstruida"
            name="areaConstruida"
            placeholder="Ex: 180"
            value={data.areaConstruida || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="areaTerreno" className="text-gray-600">
            Área Terreno (m²)
          </Label>
          <Input
            id="areaTerreno"
            name="areaTerreno"
            placeholder="Ex: 250"
            value={data.areaTerreno || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="commonArea" className="text-gray-600">
            Área Comum (m²)
          </Label>
          <Input
            id="commonArea"
            name="commonArea"
            placeholder="Ex: 50"
            value={data.commonArea || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>
      </div>
      {/* SEÇÃO: Valores */}
      <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-700">Valores</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col">
          <Label htmlFor="price" className="text-gray-600">
            Valor do Imóvel
          </Label>
          <CurrencyInput
            id="price"
            name="price"
            placeholder="R$ 0,00"
            value={data.price}
            decimalsLimit={2}
            decimalSeparator=","
            groupSeparator="."
            prefix="R$ "
            onValueChange={(value, name) => {
              onChange({
                target: {
                  name: name || "price",
                  value: value || "",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            customInput={Input}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="condominiumFee" className="text-gray-600">
            Condomínio
          </Label>
          <CurrencyInput
            id="condominiumFee"
            name="condominiumFee"
            placeholder="R$ 0,00"
            value={data.condominiumFee}
            decimalsLimit={2}
            decimalSeparator=","
            groupSeparator="."
            prefix="R$ "
            onValueChange={(value, name) => {
              onChange({
                target: {
                  name: name || "condominiumFee",
                  value: value || "",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            customInput={Input}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="iptu" className="text-gray-600">
            IPTU
          </Label>
          <CurrencyInput
            id="iptu"
            name="iptu"
            placeholder="R$ 0,00"
            value={data.iptu}
            decimalsLimit={2}
            decimalSeparator=","
            groupSeparator="."
            prefix="R$ "
            onValueChange={(value, name) => {
              onChange({
                target: {
                  name: name || "iptu",
                  value: value || "",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            customInput={Input}
            className="mt-1"
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col">
          <Label htmlFor="workFund" className="text-gray-600">
            Fundo de Obra
          </Label>
          <CurrencyInput
            id="workFund"
            name="workFund"
            placeholder="R$ 0,00"
            value={data.workFund}
            decimalsLimit={2}
            decimalSeparator=","
            groupSeparator="."
            prefix="R$ "
            onValueChange={(value, name) => {
              onChange({
                target: {
                  name: name || "workFee",
                  value: value || "",
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            customInput={Input}
            className="mt-1"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="paymentMethod" className="text-gray-600">
            Condições de Pagamento
          </Label>
          <Input
            id="paymentMethod"
            name="paymentMethod"
            placeholder="Ex: Financiamento, À vista..."
            value={data.paymentMethod || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>
      </div>
      {/* SEÇÃO: Observações */}
      <h2 className="mb-4 mt-8 text-xl font-semibold text-gray-700">
        Observações
      </h2>
      <div className="grid grid-cols-1 gap-4">
        <div className="flex flex-col">
          <Label htmlFor="externalObservation" className="text-gray-600">
            Observação Externa
          </Label>
          <textarea
            id="externalObservation"
            name="externalObservation"
            placeholder="Comentários visíveis externamente..."
            value={data.externalObservation || ""}
            onChange={onChange}
            rows={3}
            className="mt-1 w-full resize-none rounded-md border border-gray-300 p-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        <div className="flex flex-col">
          <Label htmlFor="internalObservation" className="text-gray-600">
            Observação Interna
          </Label>
          <textarea
            id="internalObservation"
            name="internalObservation"
            placeholder="Comentários internos (não visíveis ao cliente)..."
            value={data.internalObservation || ""}
            onChange={onChange}
            rows={3}
            className="mt-1 w-full resize-none rounded-md border border-gray-300 p-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <Label htmlFor="distanceFromBeach" className="text-gray-600">
              Distância da Praia (m)
            </Label>
            <Input
              id="distanceFromBeach"
              name="distanceFromBeach"
              placeholder="Ex: 300"
              value={data.distanceFromBeach || ""}
              onChange={onChange}
              className="mt-1"
            />
          </div>
          <div className="flex flex-col">
            <Label htmlFor="praia" className="text-gray-600">
              Praia
            </Label>
            <Input
              id="praia"
              name="praia"
              placeholder="Nome da praia"
              value={data.praia || ""}
              onChange={onChange}
              className="mt-1"
            />
          </div>
        </div>
        <CaptadorSelector
          setFormData={setFormData}
          selectedCaptadores={data.captadores}
        />
      </div>
      {/* SEÇÃO: Controle de Chaves */}
      <h3 className="mb-3 mt-6 text-lg font-semibold text-gray-700">
        Controle de Chaves
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col">
          <Label htmlFor="keyLocation" className="text-gray-600">
            Local das chaves
          </Label>
          <Select
            value={data.keyLocation || ""}
            onValueChange={(value: string) =>
              onChange({
                target: { name: "keyLocation", value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          >
            <SelectTrigger id="keyLocation" name="keyLocation" className="mt-1">
              <SelectValue placeholder="Selecione onde estão as chaves" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Outro">Outro</SelectItem>
              <SelectItem value="Não informado">Não informado</SelectItem>
              <SelectItem value="Corretor">Corretor</SelectItem>
              <SelectItem value="Imobiliária">Imobiliária</SelectItem>
              <SelectItem value="Proprietário">Proprietário</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col">
          <Label htmlFor="keyComplement" className="text-gray-600">
            Complemento (local das chaves)
          </Label>
          <Input
            id="keyComplement"
            name="keyComplement"
            placeholder="Ex: Zelador Ailton"
            value={data.keyComplement || ""}
            onChange={onChange}
            className="mt-1"
          />
        </div>
      </div>{" "}
    </div>
  );
}
